import { useState, useEffect } from 'react';
import AuthService, { ImpersonatorInfo } from '../services/auth.service';

export function useAuth() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMasquerading, setIsMasquerading] = useState(false);
  const [impersonator, setImpersonator] = useState<ImpersonatorInfo | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const authenticated = AuthService.isAuthenticated();
      setIsAuthenticated(authenticated);

      if (authenticated) {
        try {
          const freshUser = await AuthService.refreshUserData();
          setUser(freshUser);
          setIsMasquerading(AuthService.isMasquerading());
          setImpersonator(AuthService.getImpersonator());
        } catch (error) {
          console.error('Failed to fetch user data:', error);
          setUser(AuthService.getCurrentUser());
          setIsMasquerading(AuthService.isMasquerading());
          setImpersonator(AuthService.getImpersonator());
        }
      } else {
        setUser(null);
        setIsMasquerading(false);
        setImpersonator(null);
      }
      setLoading(false);
    };

    fetchUser();

    // Listen for localStorage changes (for login/logout events)
    const handleStorageChange = (e: StorageEvent) => {
      if (
        e.key === 'token' ||
        e.key === 'user' ||
        e.key === 'masquerade_original_token'
      ) {
        fetchUser();
      }
    };

    // Listen for custom auth events
    const handleAuthChange = () => {
      fetchUser();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-change', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-change', handleAuthChange);
    };
  }, []);

  const hasRole = (role: string) => {
    return user?.roles?.some((r: any) => r.name === role) || false;
  };

  const hasPermission = (permission: string) => {
    if (!user?.roles) return false;

    // Check if any of the user's roles have the specified permission
    return user.roles.some((role: any) =>
      role.permissions?.some((p: any) => p.name === permission)
    );
  };

  return { user, loading, isAuthenticated, isMasquerading, impersonator, hasRole, hasPermission };
}
